import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Loader2, Search, PlusCircle, Navigation, AlertCircle, ChevronRight, Download, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabase } from '../lib/supabase';
import { getDeviceType } from '../components/SmartAppBanner';
import { trackEvent } from '../lib/posthog';

// Calculate distance between two GPS coordinates in meters
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

function ScanPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bars } = useApp();
  
  const [status, setStatus] = useState('detecting');
  const [userLocation, setUserLocation] = useState(null);
  const [nearbyBars, setNearbyBars] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [scanLogged, setScanLogged] = useState(false);

  // Android PWA prompt for /scan page
  const device = getDeviceType();
  const [pwaPrompt, setPwaPrompt] = useState(null);
  const [skipBanner, setSkipBanner] = useState(false);

  useEffect(() => {
    if (device !== 'android') return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    const handler = (e) => { e.preventDefault(); setPwaPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [device]);

  // Log the QR scan to database
  const logScan = async (location, barId = null) => {
    if (scanLogged) return;
    
    try {
      await supabase.from('qr_scans').insert({
        user_location: location ? `POINT(${location.longitude} ${location.latitude})` : null,
        user_agent: navigator.userAgent,
        bar_id: barId,
        user_id: user?.id || null,
        source: 'bathroom_sticker'
      });
      setScanLogged(true);
      trackEvent('qr_code_scanned', { bar_id: barId });
    } catch (err) {
      console.error('Failed to log scan:', err);
    }
  };

  // Find bars within 50 meters
  const findNearbyBars = (userLat, userLon) => {
    const MAX_DISTANCE = 50; // meters
    
    const nearby = bars
      .filter(bar => bar.lat && bar.lng)
      .map(bar => ({
        ...bar,
        distance: calculateDistance(userLat, userLon, bar.lat, bar.lng)
      }))
      .filter(bar => bar.distance <= MAX_DISTANCE)
      .sort((a, b) => a.distance - b.distance);
    
    return nearby;
  };

  // Request location on mount
  useEffect(() => {
    if (!navigator.geolocation) {
      setStatus('error');
      setError('Geolocation is not supported by your browser');
      logScan(null);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });
        
        // Find nearby bars
        const nearby = findNearbyBars(latitude, longitude);
        setNearbyBars(nearby);
        
        if (nearby.length === 1) {
          // Exactly one bar found - redirect to it
          logScan({ latitude, longitude }, nearby[0].id);
          setStatus('found-one');
          setTimeout(() => {
            navigate(`/bar/${nearby[0].id}`);
          }, 1500);
        } else if (nearby.length > 1) {
          // Multiple bars found
          logScan({ latitude, longitude });
          setStatus('found-multiple');
        } else {
          // No bars found
          logScan({ latitude, longitude });
          setStatus('not-found');
        }
      },
      (err) => {
        console.error('Geolocation error:', err);
        if (err.code === 1) {
          // Permission denied
          setStatus('denied');
          logScan(null);
        } else {
          setStatus('error');
          setError(err.message || 'Could not get your location');
          logScan(null);
        }
      },
      options
    );
  }, [bars, navigate]);

  // Filter bars by search
  const filteredBars = useMemo(() => {
    if (!search.trim()) return bars.slice(0, 10);
    
    const searchLower = search.toLowerCase();
    return bars
      .filter(bar => 
        bar.name.toLowerCase().includes(searchLower) ||
        bar.neighborhood?.toLowerCase().includes(searchLower) ||
        bar.address?.toLowerCase().includes(searchLower)
      )
      .slice(0, 10);
  }, [bars, search]);

  // Android PWA install banner (bottom bar, overlays scan page content)
  const androidBanner = (device === 'android' && pwaPrompt && !skipBanner) ? (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-dive-dark border-t border-dive-gray p-4 animate-slide-up safe-bottom">
      <div className="max-w-md mx-auto flex items-center gap-4">
        <img src="/logo-icon.png" alt="Tilted" className="w-12 h-12 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm">Add Tilted to Home Screen</p>
          <p className="text-gray-400 text-xs">Quick access to dive bar ratings</p>
        </div>
        <button
          onClick={async () => {
            try { pwaPrompt.prompt(); await pwaPrompt.userChoice; } catch {}
            setPwaPrompt(null);
          }}
          className="flex items-center gap-1.5 px-4 py-2 bg-dive-amber text-white text-sm font-semibold rounded-lg flex-shrink-0"
        >
          <Download className="w-4 h-4" /> Install
        </button>
        <button onClick={() => setSkipBanner(true)} className="text-gray-500 p-1 flex-shrink-0">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  ) : null;

  // Detecting location
  if (status === 'detecting') {
    return (
      <div className="min-h-screen bg-dive-black flex flex-col items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 bg-dive-amber/20 rounded-full flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-dive-amber animate-spin" />
          </div>
          <h1 className="text-2xl font-display text-white mb-3">Detecting Your Location</h1>
          <p className="text-gray-400 mb-4">
            Please allow location access to find the bar you're at...
          </p>
          <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
            <MapPin className="w-4 h-4" />
            <span>Looking for nearby dive bars</span>
          </div>
        </div>
        {androidBanner}
      </div>
    );
  }

  // Found exactly one bar - redirecting
  if (status === 'found-one' && nearbyBars.length === 1) {
    const bar = nearbyBars[0];
    return (
      <div className="min-h-screen bg-dive-black flex flex-col items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
            <Navigation className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-2xl font-display text-white mb-3">Found You!</h1>
          <p className="text-gray-400 mb-2">
            You're at <strong className="text-white">{bar.name}</strong>
          </p>
          <p className="text-gray-500 text-sm mb-4">
            {Math.round(bar.distance)}m away • Taking you there now...
          </p>
          <Loader2 className="w-6 h-6 text-dive-amber animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Found multiple bars nearby
  if (status === 'found-multiple') {
    return (
      <div className="min-h-screen bg-dive-black p-6">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-dive-amber/20 rounded-full flex items-center justify-center">
              <MapPin className="w-8 h-8 text-dive-amber" />
            </div>
            <h1 className="text-2xl font-display text-white mb-2">Which bar are you at?</h1>
            <p className="text-gray-400 text-sm">
              We found {nearbyBars.length} bars near you
            </p>
          </div>

          <div className="space-y-3">
            {nearbyBars.map(bar => (
              <Link
                key={bar.id}
                to={`/bar/${bar.id}`}
                className="flex items-center justify-between p-4 bg-dive-dark border border-dive-gray rounded-xl hover:border-dive-amber transition-colors"
              >
                <div className="flex-1">
                  <h3 className="text-white font-medium">{bar.name}</h3>
                  <p className="text-gray-500 text-sm">{bar.neighborhood}</p>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <span className="text-sm">{Math.round(bar.distance)}m</span>
                  <ChevronRight className="w-5 h-5" />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-dive-gray text-center">
            <p className="text-gray-500 text-sm mb-3">Don't see your bar?</p>
            <Link
              to="/add"
              className="inline-flex items-center gap-2 text-dive-amber hover:underline"
            >
              <PlusCircle className="w-4 h-4" />
              Add a new bar
            </Link>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/list"
              className="text-gray-500 hover:text-gray-300 text-sm underline transition-colors"
            >
              Continue to site →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // No bars found nearby
  if (status === 'not-found') {
    return (
      <div className="min-h-screen bg-dive-black p-6 pb-24">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-700/50 rounded-full flex items-center justify-center">
              <MapPin className="w-8 h-8 text-gray-500" />
            </div>
            <h1 className="text-2xl font-display text-white mb-2">No Bars Found Nearby</h1>
            <p className="text-gray-400 text-sm">
              Be the first to add this bar to Tilted!
            </p>
          </div>

          <Link
            to="/add"
            className="flex items-center justify-center gap-2 w-full py-4 bg-dive-amber hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors mb-6"
          >
            <PlusCircle className="w-5 h-5" />
            Add This Bar
          </Link>

          <div className="text-center">
            <p className="text-gray-500 text-sm mb-3">Or search for an existing bar</p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search bars..."
                className="w-full pl-10 pr-4 py-3 bg-dive-dark border border-dive-gray rounded-xl text-white placeholder-gray-500 focus:border-dive-amber focus:outline-none"
              />
            </div>
            
            {search && filteredBars.length > 0 && (
              <div className="mt-3 space-y-2 text-left">
                {filteredBars.map(bar => (
                  <Link
                    key={bar.id}
                    to={`/bar/${bar.id}`}
                    className="block p-3 bg-dive-dark border border-dive-gray rounded-lg hover:border-dive-amber transition-colors"
                  >
                    <div className="text-white text-sm font-medium">{bar.name}</div>
                    <div className="text-gray-500 text-xs">{bar.neighborhood}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/list"
              className="text-gray-500 hover:text-gray-300 text-sm underline transition-colors"
            >
              Continue to site →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Location denied or error - show search
  return (
    <div className="min-h-screen bg-dive-black p-6">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-display text-white mb-2">
            {status === 'denied' ? 'Location Access Denied' : 'Location Error'}
          </h1>
          <p className="text-gray-400 text-sm">
            {status === 'denied' 
              ? 'Please enable location access or search for your bar below'
              : error || 'Could not detect your location'}
          </p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search for your bar..."
              className="w-full pl-10 pr-4 py-3 bg-dive-dark border border-dive-gray rounded-xl text-white placeholder-gray-500 focus:border-dive-amber focus:outline-none"
              autoFocus
            />
          </div>
        </div>

        {/* Search Results */}
        <div className="space-y-2 mb-6">
          {filteredBars.map(bar => (
            <Link
              key={bar.id}
              to={`/bar/${bar.id}`}
              className="flex items-center justify-between p-3 bg-dive-dark border border-dive-gray rounded-lg hover:border-dive-amber transition-colors"
            >
              <div>
                <div className="text-white text-sm font-medium">{bar.name}</div>
                <div className="text-gray-500 text-xs">{bar.neighborhood}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </Link>
          ))}
        </div>

        {/* Add new bar */}
        <Link
          to="/add"
          className="flex items-center justify-center gap-2 w-full py-4 bg-dive-gray hover:bg-gray-700 text-white font-semibold rounded-xl transition-colors"
        >
          <PlusCircle className="w-5 h-5" />
          Add a New Bar
        </Link>

        <div className="mt-6 text-center">
          <Link
            to="/list"
            className="text-gray-500 hover:text-gray-300 text-sm underline transition-colors"
          >
            Continue to site →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ScanPage;

